import { authFetch } from '../../utils/authFetch';

/** @param {string} path @param {RequestInit} [options] */
async function request(path, options) {
  const response = await authFetch(path, options);
  const text = await response.text();
  /** @type {any} */
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(`The portal could not read this response (${response.status}).`);
    }
  }
  if (!response.ok || data.success === false) {
    throw new Error(data.error || data.detail || `Request failed (${response.status})`);
  }
  return data;
}

export const ownerApi = {
  /** @param {string} rangeKey */
  analytics: (rangeKey) => request(`/api/platform/analytics?range_key=${encodeURIComponent(rangeKey)}`),
  subscribers: (cursor = '') => {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    const query = params.toString();
    return request(query ? `/api/platform/users?${query}` : '/api/platform/users');
  },
  /** @param {string} tenantId @param {number} [limit] */
  logs: (tenantId, limit = 50) =>
    request(`/api/flow/logs?tenant_id=${encodeURIComponent(tenantId)}&limit=${limit}`),
  /** @param {string} userId @param {Record<string, unknown>} changes */
  updateUser: (userId, changes) =>
    request(`/api/platform/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }),
  /** @param {string} email */
  requestPasswordReset: (email) =>
    request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  activationReadiness: () => request('/api/platform/activation-readiness'),
  messageCatalog: () => request('/api/platform/message-catalog'),
  /** @param {Record<string, unknown>} body */
  updateMessageCatalog: (body) =>
    request('/api/platform/message-catalog', { method: 'PATCH', body: JSON.stringify(body) }),
  publishMessageCatalog: () => request('/api/platform/message-catalog/publish', { method: 'POST', body: '{}' }),
  /** @param {Record<string, string>} [filters] */
  costs: (filters = {}) => {
    const params = new URLSearchParams(
      Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
    );
    const query = params.toString();
    return request(query ? `/api/platform/costs?${query}` : '/api/platform/costs');
  },
  conversionDryRun: () => request('/api/platform/credit-conversion/dry-run'),
  /** @param {string} tenantId */
  messageLedger: (tenantId) => request(`/api/platform/message-ledger/${encodeURIComponent(tenantId)}`),
  /**
   * @param {string} tenantId
   * @param {Record<string, string>} [filters]
   */
  tenantCosts: (tenantId, filters = {}) => {
    const params = new URLSearchParams(
      Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
    );
    const query = params.toString();
    return request(
      query
        ? `/api/platform/costs/tenants/${encodeURIComponent(tenantId)}?${query}`
        : `/api/platform/costs/tenants/${encodeURIComponent(tenantId)}`,
    );
  },
  /** @param {{ tenant_id: string, plan_id: string }} body */
  assignPlan: (body) =>
    request('/api/platform/plan-assignment', { method: 'POST', body: JSON.stringify(body) }),
  /** @param {{ tenant_id: string }} body */
  cancelScheduledDowngrade: (body) =>
    request('/api/platform/plan-assignment/cancel-downgrade', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** @param {{ tenant_id?: string, limit: number, reason?: string }} body */
  patchDailyEdits: (body) =>
    request('/api/platform/daily-edits', { method: 'PATCH', body: JSON.stringify(body) }),
  /** @param {string} tenantId */
  reindexTenant: (tenantId) =>
    request(`/api/platform/customer-ai-index/${encodeURIComponent(tenantId)}/reindex`, {
      method: 'POST',
      body: '{}',
    }),
  /** @param {{ tenant_id?: string, limit?: number }} [filters] */
  messageFlows: (filters = {}) => {
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries(filters)
          .filter(([, value]) => value !== undefined && value !== null && String(value) !== '')
          .map(([key, value]) => [key, String(value)]),
      ),
    );
    const query = params.toString();
    return request(query ? `/api/platform/message-flows?${query}` : '/api/platform/message-flows');
  },
  /** @param {string} tenantId @param {string} operationId */
  messageFlow: (tenantId, operationId) =>
    request(`/api/platform/message-flows/${encodeURIComponent(tenantId)}/${encodeURIComponent(operationId)}`),
  /** @param {AbortSignal} [signal] */
  brainBusinesses: (signal) => request('/api/platform/brains/businesses', signal ? { signal } : undefined),
  /**
   * @param {'customer' | 'copilot'} brain
   * @param {{ tenant_id: string, message: string, history: { role: string, text: string }[], mode?: string, phase?: string }} body
   */
  brainTurn: (brain, body) =>
    request(`/api/platform/brains/${brain}`, { method: 'POST', body: JSON.stringify(body) }),
  listKnowledge: () => request('/api/platform/copilot/knowledge'),
  /** @param {Record<string, unknown>} body */
  saveKnowledge: (body) => request('/api/platform/copilot/knowledge', { method: 'POST', body: JSON.stringify(body) }),
  /** @param {string} id */
  deleteKnowledge: (id) => request(`/api/platform/copilot/knowledge/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  listQa: () => request('/api/platform/copilot/qa'),
  /** @param {Record<string, unknown>} body */
  saveQa: (body) => request('/api/platform/copilot/qa', { method: 'POST', body: JSON.stringify(body) }),
  /** @param {string} id */
  deleteQa: (id) => request(`/api/platform/copilot/qa/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** @param {Record<string, string>} [filters] */
  listTraces: (filters = {}) => {
    const params = new URLSearchParams(
      Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined && value !== null && String(value) !== '')),
    );
    const query = params.toString();
    return request(query ? `/api/platform/copilot/traces?${query}` : '/api/platform/copilot/traces');
  },
  /** @param {string} id */
  getTrace: (id) => request(`/api/platform/copilot/traces/${encodeURIComponent(id)}`),
  audit: () => request('/api/platform/audit'),
  health: () => request('/api/platform/health'),
  copilotPricing: () => request('/api/platform/economy/copilot-pricing'),
  /** @param {Record<string, unknown>} body */
  saveCopilotPricing: (body) =>
    request('/api/platform/economy/copilot-pricing', { method: 'PUT', body: JSON.stringify(body) }),
  copilotPricingHistory: () => request('/api/platform/economy/copilot-pricing/history'),
  guestChatLimits: () => request('/api/platform/guest-chat-limits'),
  /** @param {{ daily_question_count: number, max_question_length: number }} body */
  saveGuestChatLimits: (body) =>
    request('/api/platform/guest-chat-limits', { method: 'PUT', body: JSON.stringify(body) }),
  /** @param {string} tenantId @param {string} section */
  platformCmSection: (tenantId, section) =>
    request(`/api/platform/cm/${encodeURIComponent(tenantId)}/${encodeURIComponent(section)}`),
  /**
   * @param {string} tenantId
   * @param {string} section
   * @param {Record<string, unknown>} payload
   */
  savePlatformCmSection: (tenantId, section, payload) =>
    request(`/api/platform/cm/${encodeURIComponent(tenantId)}/${encodeURIComponent(section)}`, {
      method: 'PUT',
      body: JSON.stringify({ payload }),
    }),
  aiModels: () => request('/api/platform/ai-models'),
  refreshAiModels: () => request('/api/platform/ai-models/refresh', { method: 'POST', body: '{}' }),
  /** @param {Record<string, Record<string, unknown>>} roles */
  saveAiModels: (roles) => request('/api/platform/ai-models', { method: 'PUT', body: JSON.stringify({ roles }) }),
  modelPrices: () => request('/api/platform/costs/prices'),
  /** @param {Record<string, Record<string, unknown>>} models */
  saveModelPrices: (models) =>
    request('/api/platform/costs/prices', { method: 'PUT', body: JSON.stringify({ models }) }),
};
